import React, { MouseEvent, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Toast } from 'react-bootstrap';
import moment from 'moment';
import { getInitials } from '../../../helpers/parsing-helpers';
import { useGetProfile, useCheckMaintenance } from '../../../modules/account/api';
import { useAuthActions, useAuthState } from '../../context/auth';
import { ChangePasswordModal } from '../../../modules/account/components';
import { useRootStore } from '../../context/store';
import { TOSModal } from './tos-modal';
import { useEffectOnce } from '../../hooks';
import { maintenanceStatuses } from '../../maintenance-constants';
import { Toast as ToastV2 } from '../toast';

export interface UserDropdownProps {
  className?: string;
}

export const UserDropdown: React.FC<UserDropdownProps> = ({ className }: UserDropdownProps): React.ReactElement => {
  const history = useHistory();
  const { logout } = useAuthActions();
  const [showMenu, setShowMenu] = useState(false);
  const [showTOSModal, setShowTOSModal] = useState(false);
  const [showMaintenanceToast, setShowMaintenanceToast] = useState('');
  const [changePasswordModalVisible, setChangePasswordModalVisible] = useState(false);
  const [showPasswordChangedToast, setShowPasswordChangedToast] = useState(false);
  const [, rootStoreActions] = useRootStore();
  const { profile } = useAuthState();
  const { refetch: refetchProfile } = useGetProfile({ enabled: profile === undefined });

  const { mutate: checkMaintenance } = useCheckMaintenance({
    onSuccess: (maintenanceDate) => {
      if (
        maintenanceDate &&
        maintenanceDate !== '' &&
        maintenanceDate !== 'Invalid date' &&
        typeof maintenanceDate === 'string'
      ) {
        // if maintenance date is within a day display toast
        const dateFormat = 'DD/MMM/YYYY HH:mm';
        const formatMaintenanceDate = moment(maintenanceDate).local().format(dateFormat);
        setShowMaintenanceToast(formatMaintenanceDate);
      } else if (
        maintenanceDate === maintenanceStatuses.inFiveMinutes ||
        maintenanceDate === maintenanceStatuses.inProgress ||
        (typeof maintenanceDate === 'boolean' && maintenanceDate)
      ) {
        // if maintenance date is under 5mins, logout & redirect to maintenance page
        rootStoreActions.wipeAll({});
        logout();
        history.push('/maintenance');
      }
    },
  });

  useEffect(() => {
    if (!profile) {
      refetchProfile();
    }
  });

  useEffect(() => {
    if (profile && !profile.terms_ack) {
      setShowTOSModal(true);
    }
  }, [profile]);

  useEffect(() => {
    if (profile && profile.terms_ack && profile.first_login) {
      setChangePasswordModalVisible(true);
    }
  }, [profile]);

  useEffectOnce(() => checkMaintenance());

  const onDropdownClickHandler = (event: MouseEvent<HTMLAnchorElement>): void => {
    event.preventDefault();
    setShowMenu(!showMenu);
  };

  const onProfileClickHandler = (event: MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    history.push('/profile');
  };

  const onLogoutClickHandler = (event: MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    rootStoreActions.wipeAll({});
    logout();
    history.push('/');
  };

  const getGroupName = () => {
    const groupName = profile?.groups?.[0].name;

    if (profile?.groups && profile.groups.length > 1) {
      return `${groupName} and ${profile?.groups?.length - 1} more`;
    }
    return groupName;
  };

  return (
    <div className={`dropdown ${className}`}>
      <a className="d-flex align-items-center nav-link" href="/profile" onClick={onDropdownClickHandler}>
        <span className="d-none d-sm-inline avatar avatar-rounded avatar-md d-flex align-items-center">
          {getInitials(profile?.name || 'User')}
        </span>
      </a>
      <div className={`dropdown-menu-lg dropdown-menu dropdown-menu-right ${showMenu ? 'show' : ''}`}>
        <div className="core-header-user-dropdown__dropdown-header">
          <div className="bold">
            <strong>{profile?.name || 'User'}</strong>
          </div>
          <span className="small">{getGroupName()}</span>
        </div>
        <button type="button" tabIndex={0} role="menuitem" className="dropdown-item" onClick={onProfileClickHandler}>
          View Profile
        </button>
        <button type="button" tabIndex={0} role="menuitem" className="dropdown-item" onClick={onLogoutClickHandler}>
          Logout
        </button>
      </div>
      {showTOSModal && <TOSModal onSuccess={() => setShowTOSModal(false)} />}
      {changePasswordModalVisible && (
        <ChangePasswordModal
          isFirstLogin
          onClose={() => setChangePasswordModalVisible(false)}
          onSuccess={() => {
            setChangePasswordModalVisible(false);
            setShowPasswordChangedToast(true);
          }}
        />
      )}

      <Toast
        show={showMaintenanceToast !== '' && showMaintenanceToast !== 'Invalid date'}
        delay={10000}
        style={{ position: 'fixed', top: '3vh', right: '3%', zIndex: 1000, width: '100%' }}
        className="maintenance-toast"
        onClose={() => setShowMaintenanceToast('')}
      >
        <Toast.Header>
          <i className="fa fa-info-circle me-2" aria-hidden="true" />
          Maintenance announcement
        </Toast.Header>
        <Toast.Body>
          {profile && (
            <p>
              Hello, dear <strong> {profile.name}! </strong>
            </p>
          )}
          <p>
            We apologize for the inconvenience. We have a scheduled maintenance period which starts at{' '}
            <strong style={{ color: '#0079B9' }}>{showMaintenanceToast}</strong>.
          </p>
          <p>Please check back in 30 minutes after the maintenance period starts. </p>
          <p style={{ color: '#0079B9' }}>
            Note: You will be logged out and redirected to the Maintenance page once the Maintenance starts.
          </p>
        </Toast.Body>
      </Toast>
      <ToastV2
        isVisible={showPasswordChangedToast}
        onClose={() => setShowPasswordChangedToast(false)}
        autoClose
        title="Successfully changed password"
      />
    </div>
  );
};

export default UserDropdown;
